var PerformanceMode = require("enyo-perf/PerformanceMode"),
    ScrollRepeater  = require("enyo-ui/ScrollRepeater"),
    VirtualizedRepeater,
    kind            = require("enyo/kind"),
    utils           = require("enyo/utils");

require("enyo-ui");
require("./VirtualizedRepeater.css");

/**
 * [VirtualizedRepeater]{@link enyo-ui.VirtualizedRepeater} extends
 * [ScrollRepeater]{@link enyo-ui.ScrollRepeater} with the ability to only draw
 * a subset of children controls at any given time. Enough to fill the visible
 * area of the scroller.
 *
 * Besides displaying the usual repeater children, this component will also
 * maintain as set of placeholder components to display in cases where
 * instantiating and rendering new children would negatively impact performance.
 *
 * @ui
 * @class   enyo-ui.VirtualizedRepeater
 * @extends enyo-ui.ScrollRepeater
 * @public
 */
VirtualizedRepeater = module.exports = kind(/** @lends enyo-ui.VirtualizedRepeater.prototype */ {

    /**
     * @private
     */
    name: "enyo-ui.VirtualizedRepeater",

    /**
     * @private
     */
    kind: ScrollRepeater,

    /**
     * @private
     */
    classes: "enyo-ui-virtualized-repeater",

    /**
     * The kind that will be rendered for each placeholder.
     *
     * @type {enyo.Control}
     * @public
     */
    placeholderKind: null,

    /**
     * Optional extra options to merge into the placeholder kind at creation
     * time.
     *
     * @type {Object=}
     * @public
     */
    placeholderOptions: null,

    /**
     * The number of created placeholders.
     *
     * @type {number}
     * @private
     */
    placeholderCount: 0,

    /**
     * An array of available placeholders.
     *
     * @type {?Array}
     * @private
     */
    availablePlaceholders: null,

    /**
     * A hash of used placeholders indexed by index.
     *
     * @type {?Object}
     * @private
     */
    indexToPlaceholder: null,

    /**
     * A hash of drawn children indexed by index.
     *
     * @type {?Object}
     * @private
     */
    drawnChildren: null,

    /**
     * @method
     * @private
     */
    constructor: kind.inherit(function(sup) {
        return function() {
            this.availablePlaceholders = [];
            this.indexToPlaceholder    = {};
            this.drawnChildren         = {};
            sup.apply(this, arguments);
        };
    }),

    // Placeholders
    // --------------------------------

    /**
     * Creates a placeholder control and adds it to the `availablePlaceholders`
     * stack.
     *
     * @return {?enyo.Control}  The created placeholder
     * @protected
     */
    createPlaceholder: function() {
        var childContainer     = this.getChildContainer(),
            placeholderKind    = this.getPlaceholderKind(),
            placeholderOptions = this.getPlaceholderOptions(placeholderKind),
            placeholder;

        // Create placeholder configured not to render immediately
        placeholder = childContainer.createComponent({
            name:          "repeater_placeholder_" + utils.uuid(),
            kind:          placeholderKind,
            classes:       "enyo-ui-repeater__placeholder " + (placeholderOptions.classes || ""),
            canGenerate:   false,
            repeater:      this,
            repeaterIndex: null,
            showing:       false
        }, placeholderOptions);

        // Append to stack of available placeholders
        if (placeholder) {
            ++this.placeholderCount;
            this.availablePlaceholders.push(placeholder);
        }

        return placeholder;
    },

    /**
     * Override this method if the returned kind needs to be computed.
     *
     * @param  {Object=}   record The record
     * @return {Function}  The kind constructor
     * @protected
     */
    getPlaceholderKind: function() {
        return this.placeholderKind;
    },

    /**
     * Override this method if the returned options need to change based on the
     * kind that will be used.
     *
     * @param  {Function}  kind The placeholderKind
     * @return {?Object}   The options
     * @protected
     */
    getPlaceholderOptions: function() {
        return this.placeholderOptions || {};
    },

    /**
     * Gets the placeholder currently arranged at the provided index.
     *
     * @param  {number}         index  The index
     * @return {?enyo.Control}  The placeholder if found, `null` otherwise.
     * @public
     */
    getPlaceholderAtIndex: function(index) {
        return this.indexToPlaceholder[index] || null;
    },

    /**
     * Makes sure the placeholder is removed from the arrangement, no longer
     * mapped to it's index and destroyed.
     *
     * This method does not remove the placeholder from `availablePlaceholders`
     * array. This should be done *before* destroying the placeholder.
     *
     * @param {enyo.Control}  placeholder  The placeholder
     * @protected
     */
    destroyPlaceholder: function(placeholder) {
        var index = placeholder.repeaterIndex;

        if (index !== null) {
            this.unarrangePlaceholder(placeholder);
        }
        placeholder.destroy();
        --this.placeholderCount;
    },

    /**
     * Iterates over indexed records and removes the corresponding children.
     *
     * @private
     */
    destroyPlaceholders: function() {
        var indexToPlaceholder    = this.indexToPlaceholder,
            availablePlaceholders = this.availablePlaceholders,
            index;

        this.stopDrawing();

        // Destroy arranged placeholders
        for (index in indexToPlaceholder) {
            if (indexToPlaceholder.hasOwnProperty(index)) {
                this.destroyPlaceholder(indexToPlaceholder[index]);
            }
        }

        // Destroy unarranged placeholders
        while (availablePlaceholders.length) {
            this.destroyPlaceholder(availablePlaceholders.pop());
        }
    },

    // Render, Resize, Reflow
    // --------------------------------

    /**
     * Reflowing and re-arranging a large set of controls is costly. Instead we
     * clear the repeater and re-create items as needed.
     *
     * @method
     * @private
     */
    handleResize: kind.inherit(function(sup) {
        return function() {
            this.destroyPlaceholders();
            this.destroyChildren();
            sup.apply(this, arguments);
        };
    }),

    // Draw
    // --------------------------------

    /**
     * Creates and draws as many placeholders necessary. Then creates a list of
     * indexes that need to have a child drawn. Drawing will be deferred if the
     * repeater is not absolutely showing.
     *
     * @private
     */
    draw: function() {
        var alreadyDrawing = this.drawing,
            placeholderCount,
            placeholder,
            bounds;

        // Defer if not showing
        if (!this.absoluteShowing) {
            this.addToAbsoluteShowingQueue("draw");

            return;
        }
        this.removeFromAbsoluteShowingQueue("draw");

        // Defer when in performanceMode
        if (PerformanceMode.on && this.shouldDeferDrawInPerformanceMode()) {
            this.addToPerformanceModeQueue("draw");

            return;
        }
        this.removeFromPerformanceModeQueue("draw");

        // Start draw loop if not started yet, unset the request for a new
        // draw call otherwise.
        !alreadyDrawing ? this.startDrawing() : this.set("needsDraw", false);

        // If there is no data, just update the arrangementSize and we're done.
        if (this.get("length") === 0) {
            this.updateArrangementSize();

            return;
        }

        // Find out how many placeholders we need
        placeholderCount = this.getPlaceholderCount();

        // Draw additional placeholders as needed
        while (this.placeholderCount < placeholderCount) {
            placeholder = this.createPlaceholder();
            this.drawPlaceholder(placeholder);
        }

        // Arrange all placeholders, they'll be replaced by children as they get
        // drawn
        this.drawAsync && this.arrange();

        // Get the bounds of the child container, these can be used to
        // determine if only a subset of children should be arranged.
        bounds = this.getDrawBounds();

        // Get indexes that should be arranged.
        this.undrawnIndexes = this.getArrangementIndexes(bounds).reverse();

        // Nothing to draw, stop drawing
        if (!this.undrawnIndexes.length) {
            this.stopDrawing();

            return;
        }

        // Start drawing children for individual indexes.
        this.drawAsync ? this.requestAsyncDrawNext() : this.drawNext();
    },

    /**
     * Evaluates if there are any undrawn placeholders or children. If so an
     * index is popped off the stack and a placeholders or child control is
     * created, arranged and rendered if needed.
     *
     * The childContainer will also be resized if in response to the new
     * arrangement. If drawing is async, the childContainer will be resized
     * each cycle.
     *
     * This method will be deferred the repeater is in performanceMode.
     *
     * @protected
     */
    drawNext: function() {
        var drawAsync      = this.drawAsync,
            undrawnIndexes = this.undrawnIndexes,
            index,
            record,
            child,
            placeholder;

        // Defer if not showing
        if (!this.absoluteShowing) {
            this.addToAbsoluteShowingQueue("drawNext");

            return;
        }
        this.removeFromAbsoluteShowingQueue("drawNext");

        // Defer when in performanceMode
        if (PerformanceMode.on && this.shouldDeferDrawInPerformanceMode()) {
            this.addToPerformanceModeQueue("drawNext");

            return;
        }
        this.removeFromPerformanceModeQueue("drawNext");

        if (this.drawTimeout) {
            this.drawTimeout = null;
        }

        // No undrawn indexes, we're done with this cycle
        if (undrawnIndexes.length === 0) {

            // Check if another cycle might be needed
            if (this.needsDraw) {
                this.draw();
            }
            else {
                this.stopDrawing();
            }

            return;
        }

        // Ensure a child for the next index
        index  = undrawnIndexes.pop();
        record = this.getRecordByIndex(index);

        // Only draw child if record is found
        if (record) {
            child                     = this.getChildForIndex(index);
            child                     = child ? this.updateChild(child, record) : this.createChild(record);
            child                     && this.drawChild(child);
            this.drawnChildren[index] = child;
        }

        // Unarrange any placeholder that might be associated with this index.
        placeholder = this.getPlaceholderAtIndex(index);

        if (placeholder) {
            this.unarrangePlaceholder(placeholder);
            this.availablePlaceholders.push(placeholder);
        }

        // Update arrangement
        if (!drawAsync) {

            // Schedule a redraw if arranging the child changed the flow of the
            // arrangement.
            if (child && this.arrangeChild(child, index)) {
                this.updateArrangementSize();
                this.set("needsDraw", true);
            }
        }
        else {
            this.arrange();
        }

        // Schedule next draw
        drawAsync ? this.requestAsyncDrawNext() : this.drawNext();
    },

    /**
     * Always defer drawing in PerformanceMode. Override if another strategy is
     * desired.
     *
     * @returns {bool} Whether to defer drawing the next child.
     * @protected
     */
    shouldDeferDrawInPerformanceMode: function() {
        return true;
    },

    /**
     * Draws a placeholder.
     *
     * @param  {enyo.Control}  placeholder  The placeholder
     * @private
     */
    drawPlaceholder: function(placeholder) {
        this.arranger.drawChild(placeholder);
    },

    // Arrange
    // --------------------------------

    /**
     * Arranges existing children to the right position, this should be fast and
     * should not introduce reflows. Arranging will be deferred if the repeater
     * is not absolutely showing.
     *
     * @return {boolean} `true` if the flow of the arrangement changed, `false`
     *                   otherwise.
     * @protected
     */
    arrange: function() {
        var bounds,
            arrangementIndexes,
            i,
            index,
            indexes            = {},
            indexToPlaceholder = this.indexToPlaceholder,
            drawnChildren      = this.drawnChildren,
            placeholderAtIndex,
            flowChanged        = false,
            arranged           = 0,
            childAtIndex,
            childForIndex,
            placeholderForIndex,
            scroller,
            arrangementSize;

        // Defer if not showing
        if (!this.absoluteShowing) {
            this.addToAbsoluteShowingQueue("arrange");

            return false;
        }
        this.removeFromAbsoluteShowingQueue("arrange");

        // Get the bounds of the child container, these can be used to determine
        // if only a subset of children should be arranged.
        bounds = this.getArrangementBounds();

        // Get sorted list of indexes that should be arranged.
        arrangementIndexes = this.getArrangementIndexes(bounds);

        // Within this loop we arrange children or placeholders for all
        // arrangementIndexes. At the end of the loop, we get a new sorted list
        // of arrangementIndexes. If the new list contains new indexes, we
        // arrange those too.
        while (arranged < arrangementIndexes.length) {

            // Create a hash from the arrangementIndexes so we can easily find
            // if any given index is part of the arrangement.
            for (i = 0; i < arrangementIndexes.length; i++) {
                index          = arrangementIndexes[i];
                indexes[index] = index;
            }

            // Unarrange any placeholders that are are not part of the current
            // arrangement.
            for (index in indexToPlaceholder) {
                if (indexes[index] === undefined) {
                    placeholderAtIndex = indexToPlaceholder[index];

                    if (this.unarrangePlaceholder(placeholderAtIndex)) {
                        flowChanged = true;
                    }
                    this.availablePlaceholders.push(placeholderAtIndex);
                }
            }

            // Hide any arranged children that are are not part of the current
            // arrangement.
            for (index in drawnChildren) {
                if (indexes[index] === undefined) {
                    childAtIndex         = drawnChildren[index];
                    childAtIndex.showing && childAtIndex.hide();
                    delete drawnChildren[index];
                }
            }

            // Only arrange indexes that weren't arranged in a previous cycle.
            arrangementIndexes = arrangementIndexes.slice(arranged);

            // Find out which children to arrange, and which to unarrange
            for (i = 0; i < arrangementIndexes.length; i++) {
                index              = arrangementIndexes[i];
                childAtIndex       = this.getChildAtIndex(index);
                placeholderAtIndex = this.getPlaceholderAtIndex(index);
                childForIndex      = this.getChildForIndex(index);

                // If we don't have a child, we'll use a placeholder
                if (!childForIndex) {
                    placeholderForIndex = placeholderAtIndex || this.availablePlaceholders.pop();
                }

                // Only arrange indexes that weren't arranged in a previous cycle.
                if (childAtIndex && childAtIndex !== childForIndex) {
                    if (this.unarrangeChild(childAtIndex)) {
                        flowChanged = true;
                    }
                }

                // We have a child for this index, unarrange the placeholder.
                if (placeholderAtIndex && childForIndex) {
                    if (this.unarrangePlaceholder(placeholderAtIndex)) {
                        flowChanged = true;
                    }
                    this.availablePlaceholders.push(placeholderAtIndex);
                }

                // Arrange the child for this index.
                if (childForIndex) {
                    if (this.arrangeChild(childForIndex, index)) {
                        flowChanged = true;
                    }
                    this.drawnChildren[index] = childForIndex;
                }
                else {

                    // Child is missing, a draw cycle is in order.
                    this.set("needsDraw", true);

                    // Arrange a placeholder for this index.
                    if (placeholderForIndex) {
                        if (this.arrangePlaceholder(placeholderForIndex, index)) {
                            flowChanged = true;
                        }
                    }
                }

                ++arranged;
            }

            // Get a new sorted list of indexes that should be arranged.
            arrangementIndexes = this.getArrangementIndexes(bounds);
        }

        // Ensure up-to-date arrangementSize.
        this.updateArrangementSize();

        // If the arrangement changed, we need to snap to the scroll-anchor.
        // This keeps a designated item visible within the repeater.
        if (flowChanged && this.absoluteShowing) {
            scroller        = this.getScroller();
            arrangementSize = this.get("arrangementSize");

            if ((arrangementSize.width && scroller.horizontal)
                    || (arrangementSize.height && scroller.vertical)) {
                this.snapToAnchor();
            }
        }

        return flowChanged;
    },

    /**
     * Arranges the placeholder at the provided index.
     *
     * @param  {enyo.Control}  placeholder  The placeholder
     * @param  {number}        index        The index
     * @return {boolean}       `true` if the size for this index changed.
     * @public
     */
    arrangePlaceholder: function(placeholder, index) {
        var currentIndex = placeholder.repeaterIndex,
            flowChanged;

        if (currentIndex !== index) {
            delete this.indexToPlaceholder[currentIndex];
        }
        flowChanged                    = this.arranger.arrangeChild(placeholder, index);
        !placeholder.showing           && placeholder.show();
        this.indexToPlaceholder[index] = placeholder;

        return flowChanged;
    },

    /**
     * Unarranges the placeholder.
     *
     * @param  {enyo.Control}  placeholder  The placeholder
     * @return {boolean}       `true` if the size for this index changed.
     * @public
     */
    unarrangePlaceholder: function(placeholder) {
        var flowChanged;

        delete this.indexToPlaceholder[placeholder.repeaterIndex];
        placeholder.showing && placeholder.hide();
        flowChanged = this.arranger.unarrangeChild(placeholder);

        return flowChanged;
    },

    /**
     * Gets the placeholder count needed for the current scroller dimensions.
     *
     * @return {number}  The placeholder count.
     * @protected
     */
    getPlaceholderCount: function() {
        var scrollerDimensions = this.getScroller().dimensions;

        return this.arranger.getPlaceholderCount(
            scrollerDimensions.scrollerWidth,
            scrollerDimensions.scrollerHeight
        );
    },

    /**
     * Gets the bounds of the area for which children need to be drawn. The
     * bounds should be defined in pixels, relative to the top-left corner of
     * the childContainer.
     *
     * By default we simply call `getArrangementBounds`. Override this method if
     * the area for which children should get drawn should be different than the
     * area for which children should be arranged.
     *
     * @return {Object}  The bounds
     * @public
     */
    getDrawBounds: function() {
        return this.getArrangementBounds();
    },

    /**
     * Gets the bounds of the area for which children need to be arranged. The
     * bounds should be defined in pixels, relative to the top-left corner of
     * the childContainer.
     *
     * Override this method if the top-left corner of the childContainer does
     * not align with the top-left corner of the scroller.
     *
     * @return {Object}  The bounds
     * @public
     */
    getArrangementBounds: function() {
        var scroller           = this.getScroller(),
            scrollerDimensions = scroller.dimensions,
            scrollPosition     = scroller.scrollPosition;

        return {
            top:    Math.max(0, scrollPosition.scrollTop),
            left:   Math.max(0, scrollPosition.scrollLeft),
            bottom: Math.max(0, scrollPosition.scrollTop + scrollerDimensions.scrollerHeight),
            right:  Math.max(0, scrollPosition.scrollLeft + scrollerDimensions.scrollerWidth)
        };
    },

    // Scroll
    // --------------------------------

    /**
     * Arrange children and placeholders for the new scroll bounds.
     *
     * @private
     */
    onScroll: function() {
        this.arrange();

        // Chrome sometimes doesn't update scroll position entirely
        // synchronously, causing anchor updates after applying an anchor to be
        // inaccurate. Luckily we don't need to re-evaluate the anchor so we can
        // just skip this step.
        if (!this.snappingToAnchor) {
            this.updateAnchor();
        }
        else {
            this.snappingToAnchor = false;
        }
    },

    /**
     * Updates the arrangement when a scroll interaction stops.
     *
     * @private
     */
    onScrollStop: function() {
        this.draw();
    }

});

/**
 * This method allows enyo.kind to extend the `placeholderOptions` hash when
 * creating super kinds.
 *
 * @param    {Function}  ctor   The constructor
 * @param    {Object}    props  The properties
 * @memberof Repeater
 * @static
 * @private
 */
VirtualizedRepeater.concat = function(ctor, props) {
    var proto = ctor.prototype || ctor,
        placeholderOptions;

    if (props.placeholderOptions) {
        placeholderOptions       = proto.placeholderOptions ? utils.clone(proto.placeholderOptions) : {};
        proto.placeholderOptions = utils.mixin(placeholderOptions, props.placeholderOptions);
        delete props.placeholderOptions;
    }
};
